import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../core/common/dataTable/index";
import { useDispatch } from "react-redux";
import CollapseHeader from "../../../core/common/collapse-header";
import { all_routes } from "../../router/all_routes";
import { apiService } from "../../../services/api";
import { useFornecedores } from "../../../hooks/useFornecedores";
import { Fornecedor, CreateFornecedorData } from '../../../types/fornecedor';
import { Offcanvas, Modal } from 'bootstrap';
import LocationSelector from '../contacts/components/LocationSelector';
import MaskedInput from '../contacts/components/MaskedInput';
import { modalUtils } from '../../../utils/modalUtils';
import 'bootstrap/dist/css/bootstrap.min.css';
import { ProtectedComponent } from "../../../components/ProtectedComponent";


const route = all_routes;

interface TableColumn {
  title: string;
  dataIndex?: string;
  render?: (text: any, record: Fornecedor) => React.ReactNode;
  sorter?: (a: Fornecedor, b: Fornecedor) => number;
}

export const Fornecedores = () => {
  const dispatch = useDispatch();
  const { fornecedores, loading, error, refetchFornecedores } = useFornecedores();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deletingFornecedorId, setDeletingFornecedorId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [filteredFornecedores, setFilteredFornecedores] = useState<typeof fornecedores>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Estado para controlar o fornecedor em edição
  const [editingFornecedor, setEditingFornecedor] = useState<Fornecedor | null>(null);

  // Estado inicial para um novo fornecedor
  const initialFornecedorState: CreateFornecedorData = {
    id_conta: 2, // Valor padrão ou será obtido de algum contexto
    nome: '',
    tipo_entidade: 'PJ',
    email: '',
    telefone: '',
    bairro: ''
  };

  const [newFornecedor, setNewFornecedor] = useState<CreateFornecedorData>({ ...initialFornecedorState });

  // Efeito para inicializar a lista filtrada
  useEffect(() => {
    setFilteredFornecedores(fornecedores);
  }, [fornecedores]);

  // Função para filtrar fornecedores quando o texto de busca muda (com debounce)
  useEffect(() => {
    setIsSearching(true);
    const debounceTimeout = setTimeout(() => {
      if (!searchText.trim()) {
        setFilteredFornecedores(fornecedores);
        setIsSearching(false);
        return;
      }

      const searchTerms = searchText.toLowerCase().trim().split(' ');

      const filtered = fornecedores.filter(fornecedor => {
        // Campos a serem pesquisados
        const searchableFields = [
          fornecedor.nome,
          fornecedor.razao_social,
          fornecedor.nome_fantasia,
          fornecedor.email,
          fornecedor.telefone,
          fornecedor.whatsapp,
          fornecedor.cpf,
          fornecedor.cnpj,
          fornecedor.municipio_nome,
          fornecedor.endereco,
          fornecedor.bairro
        ].filter(Boolean).map(field => field?.toLowerCase() || '');

        // Verificar se todos os termos de pesquisa estão presentes em pelo menos um dos campos
        return searchTerms.every(term =>
          searchableFields.some(field => field.includes(term))
        );
      });

      setFilteredFornecedores(filtered);
      setIsSearching(false);
    }, 300); // 300ms de delay para debounce

    return () => clearTimeout(debounceTimeout);
  }, [searchText, fornecedores]);

  // Função para limpar texto de pesquisa
  const handleClearSearch = () => {
    setSearchText('');
  };

  // Função para formatar telefone
  const formatPhone = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  // Função para formatar CPF/CNPJ
  const formatDocument = (doc: string | undefined, tipo: 'PF' | 'PJ') => {
    if (!doc) return '';
    const cleaned = doc.replace(/\D/g, '');
    if (tipo === 'PF' && cleaned.length === 11) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    if (tipo === 'PJ' && cleaned.length === 14) {
      return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    return doc;
  };

  // Função para limpar o estado de edição
  const handleCancelEdit = () => {
    setEditingFornecedor(null);
  };

  // Função para limpar o estado do novo fornecedor
  const handleCancelNew = () => {
    setNewFornecedor({ ...initialFornecedorState });
  };

  // Função para preparar a edição
  const handleEdit = (fornecedor: Fornecedor) => {
    setEditingFornecedor(fornecedor);
  };

  // Função para preparar a exclusão
  const handlePrepareDelete = (fornecedor: Fornecedor) => {
    setDeletingFornecedorId(fornecedor.id);
  };

  const columns: TableColumn[] = [
    {
      title: "Nome/Razão Social",
      render: (_, record) => (
        <div>
          <div className="fw-bold">{record.tipo_entidade === 'PJ' ? record.razao_social : record.nome}</div>
          {record.tipo_entidade === 'PJ' && record.nome_fantasia && (
            <small className="text-muted">{record.nome_fantasia}</small>
          )}
        </div>
      ),
      sorter: (a, b) => (a.tipo_entidade === 'PJ' ? a.razao_social || '' : a.nome)
        .localeCompare(b.tipo_entidade === 'PJ' ? b.razao_social || '' : b.nome),
    },
    {
      title: "Documento",
      render: (_, record) => formatDocument(record.tipo_entidade === 'PF' ? record.cpf : record.cnpj, record.tipo_entidade),
    },
    {
      title: "Contato",
      render: (_, record) => (
        <div>
          <div>{record.email}</div>
          <div>{formatPhone(record.telefone)}</div>
          {record.whatsapp && <div className="text-success"><i className="ti ti-brand-whatsapp"></i> {formatPhone(record.whatsapp)}</div>}
        </div>
      ),
    },
    {
      title: "Localização",
      render: (_, record) => (
        <div>
          {record.municipio_nome && (
            <div>{record.municipio_nome} - {record.municipio_uf}</div>
          )}
          {record.endereco && (
            <small className="text-muted">
              {record.endereco}
              {record.bairro && `, ${record.bairro}`}
              {record.numero && `, ${record.numero}`}
              {record.complemento && ` - ${record.complemento}`}
            </small>
          )}
        </div>
      ),
    },
    {
      title: "Status",
      render: (_, record) => (
        <div>
          {record.ativo ? (
            <span className="badge badge-pill badge-status bg-danger">Inativo</span>
          ) : (
            <span className="badge badge-pill badge-status bg-success">Ativo</span>
          )}
        </div>
      ),
      sorter: (a, b) => (a.ativo ? 1 : 0) - (b.ativo ? 1 : 0),
    },
    {
      title: "Ações",
      render: (_, record) => (
        <div>
          <ProtectedComponent module="CONFIG_FORNECEDORES" action="EDITAR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvas_edit"
              onClick={() => handleEdit(record)}
            >
              <i className="ti ti-edit text-blue"></i> Editar
            </Link>
          </ProtectedComponent>
          <ProtectedComponent module="CONFIG_FORNECEDORES" action="EXCLUIR">
            <Link
              to="#"
              className="dropdown-item"
              data-bs-toggle="modal"
              data-bs-target="#delete_supplier"
              onClick={() => handlePrepareDelete(record)}
            >
              <i className="ti ti-trash text-danger"></i> Excluir
            </Link>
          </ProtectedComponent>
        </div>
      ),
    }
  ];

  // Função para limpar modal
  const limparModal = (idModal: string) => {
    // Usar o utilitário corrigido
    modalUtils.fecharModal(idModal, () => {
      // Código específico para quando o modal é fechado
      if (idModal === 'success_delete_modal') {
        setDeletingFornecedorId(null);
      }
    });
  };

  // Função para criar novo fornecedor
  const handleCreateFornecedor = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);

      // Remove formatações dos campos antes de enviar
      const cleanData = {
        ...newFornecedor,
        telefone: newFornecedor.telefone?.replace(/\D/g, ''),
        whatsapp: newFornecedor.whatsapp?.replace(/\D/g, ''),
        cnpj: newFornecedor.cnpj?.replace(/\D/g, ''),
        cpf: newFornecedor.cpf?.replace(/\D/g, ''),
        cep: newFornecedor.cep?.replace(/\D/g, '')
      };

      // Validação por tipo de entidade
      if (cleanData.tipo_entidade === 'PF') {
        if (!cleanData.nome?.trim()) {
          alert('O nome é obrigatório para Pessoa Física');
          return;
        }
      } else {
        if (!cleanData.razao_social?.trim()) {
          alert('A razão social é obrigatória para Pessoa Jurídica');
          return;
        }
      }

      // Validações comuns
      if (!cleanData.email?.trim()) {
        alert('O email é obrigatório');
        return;
      }

      if (!cleanData.telefone) {
        alert('O telefone é obrigatório');
        return;
      }

      // Preparar dados base
      const baseData = {
        id_conta: cleanData.id_conta,
        tipo_entidade: cleanData.tipo_entidade,
        // Para PJ, usamos razao_social como nome, para PF usamos o nome direto
        nome: cleanData.tipo_entidade === 'PF' ? cleanData.nome?.trim() : cleanData.razao_social?.trim() || '',
        email: cleanData.email.trim(),
        telefone: cleanData.telefone,
        ...(cleanData.whatsapp && { whatsapp: cleanData.whatsapp }),
        ...(cleanData.endereco && { endereco: cleanData.endereco.trim() }),
        ...(cleanData.numero && { numero: cleanData.numero.trim() }),
        ...(cleanData.complemento && { complemento: cleanData.complemento.trim() }),
        ...(cleanData.cep && { cep: cleanData.cep }),
        ...(cleanData.estado && { estado: cleanData.estado }),
        ...(cleanData.id_municipio && { id_municipio: cleanData.id_municipio }),
        ...(cleanData.observacoes && { observacoes: cleanData.observacoes.trim() })
      };

      // Adicionar campos específicos do tipo
      const fornecedorData: CreateFornecedorData = {
        ...baseData,
        ...(cleanData.tipo_entidade === 'PF'
          ? {
            cpf: cleanData.cpf,
          }
          : {
            razao_social: cleanData.razao_social?.trim(),
            nome_fantasia: cleanData.nome_fantasia?.trim(),
            cnpj: cleanData.cnpj,
          }
        )
      };

      console.log('Dados limpos a serem enviados:', fornecedorData);

      // Envia para a API
      await apiService.createFornecedor(fornecedorData);
      await refetchFornecedores();

      // Limpar formulário
      handleCancelNew();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_supplier');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Limpar qualquer efeito do offcanvas/modal antes de mostrar o modal de sucesso
      setTimeout(() => {
        // Limpeza completa de modais e backdrops
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open', 'offcanvas-open');
        document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop').forEach(backdrop => backdrop.remove());

        // Mostra modal de sucesso usando o utilitário
        modalUtils.abrirModal('success_modal');
      }, 300);

    } catch (error) {
      console.error('Erro ao criar fornecedor:', error);
      if (error instanceof Error) {
        alert(`Erro ao criar o fornecedor: ${error.message}`);
      } else {
        alert('Erro ao criar o fornecedor. Por favor, verifique os dados e tente novamente.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };


  // Função para atualizar fornecedor
  const handleUpdateFornecedor = async () => {
    if (!editingFornecedor?.id) return;

    try {
      setIsSubmitting(true);
      await apiService.updateFornecedor(editingFornecedor.id, editingFornecedor);
      await refetchFornecedores();

      // Fecha o offcanvas
      const offcanvasElement = document.getElementById('offcanvas_edit');
      if (offcanvasElement) {
        const offcanvasInstance = Offcanvas.getInstance(offcanvasElement);
        if (offcanvasInstance) {
          offcanvasInstance.hide();
        }
      }

      // Limpar qualquer efeito do offcanvas/modal antes de mostrar o modal de sucesso
      setTimeout(() => {
        // Limpeza completa de modais e backdrops
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open', 'offcanvas-open');
        document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop').forEach(backdrop => backdrop.remove());

        // Mostra modal de sucesso usando o utilitário
        modalUtils.abrirModal('success_edit_modal');
      }, 300);

      handleCancelEdit();
    } catch (error) {
      console.error('Erro ao atualizar fornecedor:', error);
      alert('Erro ao atualizar o fornecedor. Por favor, tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Função para deletar fornecedor
  const handleDeleteFornecedor = async () => {
    if (!deletingFornecedorId) return;

    try {
      setIsDeleting(true);
      await apiService.deleteFornecedor(deletingFornecedorId);
      await refetchFornecedores();

      // Fechar o modal de confirmação usando o utilitário
      modalUtils.fecharModal('delete_supplier');

      // Limpar qualquer efeito do modal antes de mostrar o modal de sucesso
      setTimeout(() => {
        // Limpeza completa de modais e backdrops
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.classList.remove('modal-open');
        document.querySelectorAll('.modal-backdrop').forEach(backdrop => backdrop.remove());

        // Mostra modal de sucesso
        modalUtils.abrirModal('success_delete_modal');
      }, 300);

    } catch (error) {
      console.error('Erro ao excluir fornecedor:', error);
      alert('Erro ao excluir o fornecedor. Por favor, tente novamente.');

      // Garantir limpeza mesmo em caso de erro
      modalUtils.limparEfeitosModal();
    } finally {
      setIsDeleting(false);
    }
  };

  useEffect(() => {
    return () => {
      // Cleanup ao desmontar o componente
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('modal-open', 'offcanvas-open');
      const backdrops = document.querySelectorAll('.modal-backdrop, .offcanvas-backdrop');
      backdrops.forEach(backdrop => backdrop.remove());
    };
  }, []);

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              <div className="card">
                <div className="card-body">
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Cabeçalho da Página */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-4">
                    <h4 className="page-title">
                      Fornecedores<span className="count-title">{filteredFornecedores.length} fornecedores</span>
                    </h4>
                  </div>
                  <div className="col-8 text-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div className="card-header">
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="search-input position-relative">
                        <div className="icon-form mb-3 mb-sm-0">
                          <span className="form-icon">
                            {isSearching ? (
                              <i className="spinner-border spinner-border-sm" role="status" />
                            ) : (
                              <i className="ti ti-search" />
                            )}
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Pesquisar Fornecedor"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                          {searchText && (
                            <button
                              className="btn btn-sm position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                              onClick={handleClearSearch}
                              type="button"
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Filtros e Ações */}
                  <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-2 mb-4">
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      {/* Botão Ordenar */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-sort-ascending-2 me-2" />
                          Ordenar
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome A-Z
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Nome Z-A
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Mais Recentes
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-circle-chevron-right me-1" />
                            Mais Antigos
                          </Link>
                        </div>
                      </div>

                      {/* Filtro Tipo */}
                      <div className="dropdown me-2">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-user me-2" />
                          Tipo
                        </Link>
                        <div className="dropdown-menu">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-user me-1" />
                            Pessoa Física
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-building me-1" />
                            Pessoa Jurídica
                          </Link>
                        </div>
                      </div>

                      {/* Botão Exportar */}
                      <div className="dropdown">
                        <Link to="#" className="dropdown-toggle" data-bs-toggle="dropdown">
                          <i className="ti ti-package-export me-2" />
                          Exportar
                        </Link>
                        <div className="dropdown-menu dropdown-menu-end">
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-pdf text-danger me-1" />
                            Exportar como PDF
                          </Link>
                          <Link to="#" className="dropdown-item">
                            <i className="ti ti-file-type-xls text-green me-1" />
                            Exportar como Excel
                          </Link>
                        </div>
                      </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="d-flex align-items-center flex-wrap row-gap-2">
                      <ProtectedComponent module="CONFIG_FORNECEDORES" action="CRIAR">
                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="offcanvas"
                          data-bs-target="#offcanvas_supplier"
                        >
                          <i className="ti ti-building-store me-2" />
                          Adicionar Fornecedor
                        </Link>
                      </ProtectedComponent>
                    </div>
                  </div>

                  {/* Tabela de Fornecedores */}
                  <div className="table-responsive custom-table">
                    {!isSearching && searchText && filteredFornecedores.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="mb-3">
                          <i className="ti ti-search-off fs-3 text-muted"></i>
                        </div>
                        <h5 className="text-muted">Nenhum fornecedor encontrado</h5>
                        <p className="text-muted">Sua pesquisa por "{searchText}" não retornou resultados.</p>
                        <button
                          className="btn btn-outline-primary mt-2"
                          onClick={handleClearSearch}
                        >
                          <i className="ti ti-x me-1"></i>
                          Limpar pesquisa
                        </button>
                      </div>
                    ) : (
                      <Table
                        dataSource={filteredFornecedores.map(fornecedor => ({
                          ...fornecedor,
                          key: fornecedor.id.toString() // Adiciona a propriedade key diretamente no objeto
                        }))}
                        columns={columns}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Offcanvas Adicionar Novo Fornecedor */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_supplier">
        <div className="offcanvas-header border-bottom">
          <h4>Adicionar Novo Fornecedor</h4>
          <button type="button" className="btn-close" data-bs-dismiss="offcanvas">
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={handleCreateFornecedor}>
            <div className="mb-3">
              <label className="col-form-label">
                Tipo de Pessoa <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={newFornecedor.tipo_entidade}
                onChange={(e) => setNewFornecedor(prev => ({
                  ...prev,
                  tipo_entidade: e.target.value as 'PF' | 'PJ'
                }))}
              >
                <option value="PF">Pessoa Física</option>
                <option value="PJ">Pessoa Jurídica</option>
              </select>
            </div>

            {newFornecedor.tipo_entidade === 'PF' ? (
              <>
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={newFornecedor.nome}
                    onChange={(e) => setNewFornecedor(prev => ({
                      ...prev,
                      nome: e.target.value
                    }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">CPF</label>
                  <MaskedInput
                    value={newFornecedor.cpf || ''}
                    onChange={(value) => setNewFornecedor(prev => ({
                      ...prev,
                      cpf: value
                    }))}
                    mask="cpf"
                    placeholder="000.000.000-00"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <label className="col-form-label">
                    Razão Social <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={newFornecedor.razao_social || ''}
                    onChange={(e) => setNewFornecedor(prev => ({
                      ...prev,
                      razao_social: e.target.value
                    }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">Nome Fantasia</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newFornecedor.nome_fantasia || ''}
                    onChange={(e) => setNewFornecedor(prev => ({
                      ...prev,
                      nome_fantasia: e.target.value
                    }))}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">CNPJ</label>
                  <MaskedInput
                    value={newFornecedor.cnpj || ''}
                    onChange={(value) => setNewFornecedor(prev => ({
                      ...prev,
                      cnpj: value
                    }))}
                    mask="cnpj"
                    placeholder="00.000.000/0000-00"
                  />
                </div>
              </>
            )}

            <div className="mb-3">
              <label className="col-form-label">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control"
                value={newFornecedor.email}
                onChange={(e) => setNewFornecedor(prev => ({
                  ...prev,
                  email: e.target.value
                }))}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Telefone <span className="text-danger">*</span>
              </label>
              <MaskedInput
                value={newFornecedor.telefone || ''}
                onChange={(value) => setNewFornecedor(prev => ({
                  ...prev,
                  telefone: value
                }))}
                mask="phone"
                placeholder="(00) 00000-0000"
                required
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">WhatsApp</label>
              <MaskedInput
                value={newFornecedor.whatsapp || ''}
                onChange={(value) => setNewFornecedor(prev => ({
                  ...prev,
                  whatsapp: value
                }))}
                mask="phone"
                placeholder="(00) 00000-0000"
              />
            </div>

            <LocationSelector
              selectedState={newFornecedor.estado}
              selectedMunicipality={newFornecedor.id_municipio ? Number(newFornecedor.id_municipio) : undefined}
              onStateChange={(estado) => {
                setNewFornecedor(prev => ({
                  ...prev,
                  estado: estado,
                  id_municipio: undefined
                }));
              }}
              onMunicipalityChange={(municipioId) => {
                setNewFornecedor(prev => ({
                  ...prev,
                  id_municipio: Number(municipioId)
                }));
              }}
              apiService={apiService}
            />

            <div className="mb-3">
              <label className="col-form-label">Endereço</label>
              <input
                type="text"
                className="form-control"
                value={newFornecedor.endereco || ''}
                onChange={(e) => setNewFornecedor(prev => ({
                  ...prev,
                  endereco: e.target.value
                }))}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Bairro</label>
              <input
                type="text"
                className="form-control"
                value={newFornecedor.bairro || ''}
                onChange={(e) => setNewFornecedor(prev => ({
                  ...prev,
                  bairro: e.target.value
                }))}
              />
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <label className="col-form-label">Número</label>
                <input
                  type="text"
                  className="form-control"
                  value={newFornecedor.numero || ''}
                  onChange={(e) => setNewFornecedor(prev => ({
                    ...prev,
                    numero: e.target.value
                  }))}
                />
              </div>
              <div className="col-8">
                <label className="col-form-label">Complemento</label>
                <input
                  type="text"
                  className="form-control"
                  value={newFornecedor.complemento || ''}
                  onChange={(e) => setNewFornecedor(prev => ({
                    ...prev,
                    complemento: e.target.value
                  }))}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="col-form-label">CEP</label>
              <MaskedInput
                value={newFornecedor.cep || ''}
                onChange={(value) => setNewFornecedor(prev => ({
                  ...prev,
                  cep: value
                }))}
                mask="cep"
                placeholder="00000-000"
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Documento</label>
              <input
                type="text"
                className="form-control"
                value={newFornecedor.documento || ''}
                onChange={(e) => setNewFornecedor(prev => ({
                  ...prev,
                  documento: e.target.value
                }))}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Observações</label>
              <textarea
                className="form-control"
                value={newFornecedor.observacoes || ''}
                onChange={(e) => setNewFornecedor(prev => ({
                  ...prev,
                  observacoes: e.target.value
                }))}
              />
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                disabled={isSubmitting}
                onClick={handleCancelNew} // Limpar formulário ao cancelar
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Criando...
                  </>
                ) : (
                  'Criar'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>


      {/* Offcanvas Editar Fornecedor */}
      <div className="offcanvas offcanvas-end offcanvas-large" tabIndex={-1} id="offcanvas_edit">
        <div className="offcanvas-header border-bottom">
          <h4>Editar Fornecedor</h4>
          <button
            type="button"
            className="btn-close"
            data-bs-dismiss="offcanvas"
            onClick={handleCancelEdit}
          >
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="offcanvas-body">
          <form onSubmit={(e) => {
            e.preventDefault();
            handleUpdateFornecedor();
          }}>
            <div className="mb-3">
              <label className="col-form-label">
                Tipo de Pessoa <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={editingFornecedor?.tipo_entidade || 'PJ'}
                onChange={(e) => setEditingFornecedor(prev => prev ? {
                  ...prev,
                  tipo_entidade: e.target.value as 'PF' | 'PJ'
                } : null)}
              >
                <option value="PF">Pessoa Física</option>
                <option value="PJ">Pessoa Jurídica</option>
              </select>
            </div>

            {editingFornecedor?.tipo_entidade === 'PF' ? (
              <>
                <div className="mb-3">
                  <label className="col-form-label">
                    Nome <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingFornecedor.nome || ''}
                    onChange={(e) => setEditingFornecedor(prev => prev ? {
                      ...prev,
                      nome: e.target.value
                    } : null)}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">CPF</label>
                  <MaskedInput
                    value={editingFornecedor?.cpf || ''}
                    onChange={(value) => setEditingFornecedor(prev => prev ? {
                      ...prev,
                      cpf: value
                    } : null)}
                    mask="cpf"
                    placeholder="000.000.000-00"
                  />
                </div>
              </>
            ) : (
              <>
                <div className="mb-3">
                  <label className="col-form-label">
                    Razão Social <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingFornecedor?.razao_social || ''}
                    onChange={(e) => setEditingFornecedor(prev => prev ? {
                      ...prev,
                      razao_social: e.target.value
                    } : null)}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">Nome Fantasia</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editingFornecedor?.nome_fantasia || ''}
                    onChange={(e) => setEditingFornecedor(prev => prev ? {
                      ...prev,
                      nome_fantasia: e.target.value
                    } : null)}
                  />
                </div>
                <div className="mb-3">
                  <label className="col-form-label">CNPJ</label>
                  <MaskedInput
                    value={editingFornecedor?.cnpj || ''}
                    onChange={(value) => setEditingFornecedor(prev => prev ? {
                      ...prev,
                      cnpj: value
                    } : null)}
                    mask="cnpj"
                    placeholder="00.000.000/0000-00"
                  />
                </div>
              </>
            )}

            <div className="mb-3">
              <label className="col-form-label">
                Email <span className="text-danger">*</span>
              </label>
              <input
                type="email"
                className="form-control"
                value={editingFornecedor?.email || ''}
                onChange={(e) => setEditingFornecedor(prev => prev ? {
                  ...prev,
                  email: e.target.value
                } : null)}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">
                Telefone <span className="text-danger">*</span>
              </label>
              <MaskedInput
                value={editingFornecedor?.telefone || ''}
                onChange={(value) => setEditingFornecedor(prev => prev ? {
                  ...prev,
                  telefone: value
                } : null)}
                mask="phone"
                placeholder="(00) 00000-0000"
                required
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">WhatsApp</label>
              <MaskedInput
                value={editingFornecedor?.whatsapp || ''}
                onChange={(value) => setEditingFornecedor(prev => prev ? {
                  ...prev,
                  whatsapp: value
                } : null)}
                mask="phone"
                placeholder="(00) 00000-0000"
              />
            </div>

            <LocationSelector
              selectedState={editingFornecedor?.estado}
              selectedMunicipality={editingFornecedor?.id_municipio}
              onStateChange={(estado) => setEditingFornecedor(prev => prev ? {
                ...prev,
                estado: estado,
                id_municipio: undefined
              } : null)}
              onMunicipalityChange={(municipioId) => setEditingFornecedor(prev => prev ? {
                ...prev,
                id_municipio: Number(municipioId)
              } : null)}
              apiService={apiService}
              isEditing={false}
            />

            <div className="mb-3">
              <label className="col-form-label">Endereço</label>
              <input
                type="text"
                className="form-control"
                value={editingFornecedor?.endereco || ''}
                onChange={(e) => setEditingFornecedor(prev => prev ? {
                  ...prev,
                  endereco: e.target.value
                } : null)}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Bairro</label>
              <input
                type="text"
                className="form-control"
                value={editingFornecedor?.bairro || ''}
                onChange={(e) => setEditingFornecedor(prev => prev ? {
                  ...prev,
                  bairro: e.target.value
                } : null)}
              />
            </div>

            <div className="row mb-3">
              <div className="col-4">
                <label className="col-form-label">Número</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingFornecedor?.numero || ''}
                  onChange={(e) => setEditingFornecedor(prev => prev ? {
                    ...prev,
                    numero: e.target.value
                  } : null)}
                />
              </div>
              <div className="col-8">
                <label className="col-form-label">Complemento</label>
                <input
                  type="text"
                  className="form-control"
                  value={editingFornecedor?.complemento || ''}
                  onChange={(e) => setEditingFornecedor(prev => prev ? {
                    ...prev,
                    complemento: e.target.value
                  } : null)}
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="col-form-label">CEP</label>
              <MaskedInput
                value={editingFornecedor?.cep || ''}
                onChange={(value) => setEditingFornecedor(prev => prev ? {
                  ...prev,
                  cep: value
                } : null)}
                mask="cep"
                placeholder="00000-000"
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Documento</label>
              <input
                type="text"
                className="form-control"
                value={editingFornecedor?.documento || ''}
                onChange={(e) => setEditingFornecedor(prev => prev ? {
                  ...prev,
                  documento: e.target.value
                } : null)}
              />
            </div>

            <div className="mb-3">
              <label className="col-form-label">Observações</label>
              <textarea
                className="form-control"
                value={editingFornecedor?.observacoes || ''}
                onChange={(e) => setEditingFornecedor(prev => prev ? {
                  ...prev,
                  observacoes: e.target.value
                } : null)}
              />
            </div>

            <div className="d-flex align-items-center justify-content-end">
              <button
                type="button"
                className="btn btn-light me-2"
                data-bs-dismiss="offcanvas"
                onClick={handleCancelEdit}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Salvando...
                  </>
                ) : (
                  'Salvar Alterações'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Modal de Confirmação de Exclusão */}
      <div className="modal fade" id="delete_supplier" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="text-center">
                <div className="avatar avatar-xl bg-danger-light rounded-circle mb-3">
                  <i className="ti ti-trash-x fs-36 text-danger" />
                </div>
                <h4 className="mb-2">Remover Fornecedor?</h4>
                <p className="mb-0">
                  Tem certeza que deseja remover o fornecedor selecionado?
                </p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                    disabled={isDeleting}
                    onClick={() => setDeletingFornecedorId(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleDeleteFornecedor}
                    disabled={isDeleting}
                  >
                    {isDeleting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Excluindo...
                      </>
                    ) : (
                      'Sim, Deletar'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Criação */}
      <div className="modal fade custom-modal" id="success_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Fornecedor Criado com Sucesso!</h3>
                <p>O fornecedor foi criado e já está disponível para uso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Edição */}
      <div className="modal fade custom-modal" id="success_edit_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Fornecedor Atualizado com Sucesso!</h3>
                <p>As alterações foram salvas com sucesso.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_edit_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Sucesso - Exclusão */}
      <div className="modal fade custom-modal" id="success_delete_modal" tabIndex={-1} role="dialog" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body">
              <div className="success-message text-center">
                <div className="avatar avatar-xl bg-success-light rounded-circle mb-3">
                  <i className="ti ti-check-check fs-36 text-success" />
                </div>
                <h3>Fornecedor Excluído com Sucesso!</h3>
                <p>O fornecedor foi removido permanentemente do sistema.</p>
                <div className="d-flex align-items-center justify-content-center mt-4">
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => modalUtils.fecharModal('success_delete_modal')}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};